import { format, isValid, parse, parseISO } from "date-fns";
import { fullDateLongFormat } from "@common/helpers/date";

export type SpreadsheetValue = string | number | null;

const FORMULA_PREFIXES = ["=", "+", "-", "@"];

/**
 * Neutralizes spreadsheet formula injection from user-controlled values by
 * quoting cells that start with a formula prefix, so Excel/Sheets render them
 * as text instead of evaluating them.
 */
export function neutralizeSpreadsheetFormula<T extends SpreadsheetValue>(
    value: T,
): T | string {
    if (typeof value !== "string") {
        return value;
    }

    const trimmed = value.trimStart();
    const hasFormulaPrefix = FORMULA_PREFIXES.some((prefix) =>
        trimmed.startsWith(prefix),
    );

    return hasFormulaPrefix ? `'${value}` : value;
}

export function formatSpreadsheetDateValue(date: Date): string {
    return format(date, fullDateLongFormat);
}

export function formatISODateToSpreadsheetValue(
    isoDate: string | null,
): string | null {
    if (!isoDate) {
        return null;
    }
    const date = parseISO(isoDate);
    return isValid(date) ? formatSpreadsheetDateValue(date) : null;
}

export function parseSpreadsheetNumericCellValue(
    value: number | string | null | undefined,
): number | null {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") {
            return null;
        }
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

export function parseSpreadsheetDateValue(value: string | null): Date | null {
    if (!value) {
        return null;
    }

    const date = parse(value, fullDateLongFormat, new Date());

    // check if date is valid and if it's not a 2-digit year
    if (!isValid(date) || date.getFullYear() < 100) {
        return null;
    }

    return date;
}
